import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axios";
import { isDarkColor } from "../utils/routeColor";
import { canUseAnalysisInternals } from "../utils/analysisAccess";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };
const DAY_TYPES = ["weekday", "sat", "holiday"];

export default function AnalysisPage({ currentUser }) {
  const navigate = useNavigate();

  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [busNumber, setBusNumber] = useState("");

  // 路線の一覧はダイヤ種別ごとに中身が違う。
  // 土日祝しか走らない路線（アイランド海の中道など）は平日の一覧に
  // 一切出ないので、ここで種別を選べないと入口から辿り着けない。
  const [routeDayType, setRouteDayType] = useState("weekday");

  useEffect(() => {
    axios
      .get("/analysis/duties/availability")
      .then((res) => setDepots(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDepots([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setLoadingRoutes(true);
    axios
      .get(`/analysis/routes?day_type=${routeDayType}`)
      .then((res) => setRoutes(res.data?.routes ?? []))
      .catch(() => setRoutes([]))
      .finally(() => setLoadingRoutes(false));
  }, [routeDayType]);

  // API は sort_id 順（営業所順 → 路線順）で返す。その順を崩さずに
  // 営業所ごとへ束ねたいので Map を使う。
  const groupedRoutes = useMemo(() => {
    const map = new Map();
    for (const r of routes) {
      const key = r.depot || "その他";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries());
  }, [routes]);

  function goToBus(e) {
    e.preventDefault();
    const trimmed = busNumber.trim();
    if (trimmed) navigate(`/analysis/buses/${encodeURIComponent(trimmed)}`);
  }

  return (
    <div className="anl-page">
      <div className="anl-header">
        <h2 className="anl-title">運用解析</h2>
        <p className="anl-subtitle">
          投稿された目撃情報をもとに、1台の車両が1日に担当する便の並び（乗番）を推定しています。
        </p>
      </div>

      <div className="anl-warning">
        <strong>本機能は試験運用中です。</strong><br />
        乗番および予測は、いずれも目撃情報からの推定です。目撃情報の無い便は反映されず、
        運用変更や運休が生じた場合は実際と異なります。<strong>参考情報としてご利用ください。</strong><br />
        {/* 車号を引いても何も出ない営業所がある。**理由を先に言う。**
            不具合に見えるのを防ぐため、断り書きと同じ枠に置く。
            **営業所名は決め打ちしない**（乗番がある営業所を API から。
            2026-09-26 に土井を足したとき、ここだけアイランドのままになりかけた） */}
        {depots.length > 0 && (
          <>現時点では{depots.map((d) => d.depot_name || d.depot).join("・")}のみ対応しています。</>
        )}
      </div>

      {/* **いちばん上に置く。** 車号や路線を知らなくても開けるのはここだけで、
          「何か起きていないか」を見に来た人の入口になる。 */}
      <div className="anl-card">
        <div className="anl-card__header">本日のできごと</div>
        <div className="anl-card__body">
          <button className="anl-btn" onClick={() => navigate("/analysis/events")}>
            本日のできごとを見る
          </button>
          <p className="anl-note">
            車両交代・初入線・久しぶりの目撃を拾って並べます。日付を遡ることもできます。
          </p>
        </div>
      </div>

      {/* **調べる画面と、解析そのものを見る画面は分けてある。**
          ここから下は乗番の中身を見るところ、下の「解析の様子」は
          その乗番が信用できるかどうかを見るところ。混ぜると、
          利用者向けの画面に運用の内輪の数字が紛れ込む。 */}
      {/* **車号を知らなくても開ける入口**（260905追加）。
          「いまそこに行けば何が来るか」は、車号から引く画面では答えられない */}
      <div className="anl-card">
        <div className="anl-card__header">バス停から探す</div>
        <div className="anl-card__body">
          <button className="anl-btn" onClick={() => navigate("/analysis/stops")}>
            バス停を選ぶ
          </button>
          <p className="anl-note">
            指定したバス停をこれから通る便の内、<strong>どの車両が入るか判明している便</strong>を
            時刻順に並べます。時刻表の代わりにはなりません。
          </p>
        </div>
      </div>

      <div className="anl-card">
        <div className="anl-card__header">車号から検索</div>
        <div className="anl-card__body">
          <form className="anl-search" onSubmit={goToBus}>
            <input
              className="anl-input anl-input--short"
              type="text"
              inputMode="numeric"
              /* 既定の 20 文字ぶんは要らない。狭い画面でボタンが折り返す */
              size={8}
              placeholder="例: 2603"
              value={busNumber}
              onChange={(e) => setBusNumber(e.target.value)}
            />
            <button className="anl-btn" type="submit" disabled={!busNumber.trim()}>
              運用を表示
            </button>
          </form>
          <p className="anl-note">
            当該車両が本日どの乗番で運用されているか、および以降の運行予定を表示します。
          </p>
        </div>
      </div>

      <div className="anl-card">
        <div className="anl-card__header">
          路線から検索
          {!loadingRoutes && <span style={{ fontWeight: 400, color: "#6b7280" }}>{routes.length}路線</span>}
        </div>
        <div className="anl-card__body">
          <div className="anl-tabs">
            {DAY_TYPES.map((key) => (
              <button
                key={key}
                className={`anl-tab ${routeDayType === key ? "anl-tab--active" : ""}`}
                onClick={() => setRouteDayType(key)}
              >
                {DAY_TYPE_LABEL[key]}
              </button>
            ))}
          </div>
          {loadingRoutes ? (
            <p className="anl-empty">読み込み中…</p>
          ) : routes.length === 0 ? (
            <p className="anl-empty">
              {DAY_TYPE_LABEL[routeDayType]}の便が登録されている路線はありません。<br />
              目撃情報が蓄積されると表示されます。
            </p>
          ) : (
            <>
              {/* **営業所ごとにまとめる。**解析する営業所が増えると、
                  同じ名前の路線が並ぶ（香椎幹線はアイランドにも赤間にもある）。
                  ひと続きに並べると、どれがどの営業所か見分けが付かない。
                  1 営業所しか無いうちは見出しを出さない。 */}
              {groupedRoutes.map(([depot, list]) => (
                <div key={depot} className="anl-route-group">
                  {groupedRoutes.length > 1 && (
                    <div className="anl-route-group__name">{depot}</div>
                  )}
                  <div className="anl-route-chips">
                    {list.map((r) => (
                      <button
                        key={r.route_id}
                        className="anl-route-badge anl-route-badge--lg"
                        style={{
                          background: r.color || "#6b7280",
                          color: isDarkColor(r.color) ? "#fff" : "#111827",
                        }}
                        /* 選んだ種別を詳細ページにも渡す。渡さないと
                           詳細側が平日に戻り、土日祝の路線が空表示になる。 */
                        onClick={() => navigate(`/analysis/routes/${r.route_id}?day_type=${routeDayType}`)}
                        title={`${r.route_full_name}（${r.depot}） · ${r.trip_count}便`}
                      >
                        {r.route}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <p className="anl-note">
                当該路線の便を時刻順に並べ、各便に入ることの多い車両を表示します。
              </p>
            </>
          )}
        </div>
      </div>

      <div className="anl-card">
        <div className="anl-card__header">
          営業所から検索
          {!loading && <span style={{ fontWeight: 400, color: "#6b7280" }}>{depots.length}営業所</span>}
        </div>
        <div className="anl-card__body">
          {loading ? (
            <p className="anl-empty">読み込み中…</p>
          ) : depots.length === 0 ? (
            <p className="anl-empty">
              乗番を推定できた営業所はありません。<br />
              目撃情報が蓄積されると表示されます。
            </p>
          ) : (
            <div className="anl-depot-grid">
              {depots.map((d) => (
                <button
                  key={d.depot}
                  className="anl-depot-card"
                  onClick={() => navigate(`/analysis/duties?depot=${encodeURIComponent(d.depot)}`)}
                >
                  <div className="anl-depot-card__name">{d.depot}</div>
                  <div className="anl-depot-card__meta">
                    {Object.entries(d.day_types)
                      .map(([key, count]) => `${DAY_TYPE_LABEL[key] ?? key} ${count}`)
                      .join(" / ")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* **解析の様子は運用解析より狭い。**乗番が正しいかどうかを見る側で、
          運用の内輪の数字が並ぶ。入口ごと出さない（管理画面と同じ扱い） */}
      {canUseAnalysisInternals(currentUser) && (
      <div className="anl-card">
        <div className="anl-card__header">解析の様子</div>
        <div className="anl-card__body">
          <div className="anl-flag__actions">
            <button className="anl-btn anl-btn--quiet" onClick={() => navigate("/analysis/audit")}>
              要確認
            </button>
            <button className="anl-btn anl-btn--quiet" onClick={() => navigate("/analysis/health")}>
              健康診断
            </button>
            <button className="anl-btn anl-btn--quiet" onClick={() => navigate("/analysis/coverage")}>
              観測の穴
            </button>
          </div>
          <p className="anl-note">
            <strong>推定がどれくらい確かかを見るところです。</strong>
            つじつまの合わない目撃、予測の当たりぐあいの推移、拾えていない便を出します。
          </p>
        </div>
      </div>
      )}
    </div>
  );
}
